export type PaymentOutcome = 'paid' | 'in_progress' | 'rejected';

export const PAID: PaymentOutcome = 'paid';
export const IN_PROGRESS: PaymentOutcome = 'in_progress';
export const REJECTED: PaymentOutcome = 'rejected';

type OutcomeByStatus = Record<string, PaymentOutcome>;

const FLYWIRE_CHECKOUT_OUTCOMES: OutcomeByStatus = {
  success: PAID,
  pending: IN_PROGRESS,
  error: REJECTED,
};

const FLYWIRE_PAYMENT_OUTCOMES: OutcomeByStatus = {
  guaranteed: PAID,
  delivered: PAID,
  initiated: IN_PROGRESS,
  processed: IN_PROGRESS,
  cancelled: REJECTED,
  canceled: REJECTED,
  failed: REJECTED,
  reversed: REJECTED,
};

const FLYWIRE_OUTCOMES: OutcomeByStatus = {
  ...FLYWIRE_PAYMENT_OUTCOMES,
  ...FLYWIRE_CHECKOUT_OUTCOMES,
};

const STRIPE_OUTCOMES: OutcomeByStatus = {
  succeeded: PAID,
  processing: IN_PROGRESS,
  canceled: REJECTED,
  requires_payment_method: REJECTED,
};

const OUTCOMES_BY_GATEWAY: Record<string, OutcomeByStatus> = {
  flywire: FLYWIRE_OUTCOMES,
  stripe: STRIPE_OUTCOMES,
};

const GATEWAY_AGNOSTIC_OUTCOMES: OutcomeByStatus = {
  success: PAID,
  succeeded: PAID,
  error: REJECTED,
  failed: REJECTED,
};

const CARD_METHODS: readonly string[] = ['card', 'credit_card', 'recurring'];

const BANK_TRANSFER_METHODS: readonly string[] = ['bank_transfer', 'offline'];

const FLYWIRE_BANK_TRANSFER_STATUS = 'pending';

const normalize = (value?: string): string => (value ?? '').trim().toLowerCase();

export const resolvePaymentOutcome = (gateway?: string, status?: string): PaymentOutcome => {
  const key = normalize(status);
  return OUTCOMES_BY_GATEWAY[normalize(gateway)]?.[key] ?? GATEWAY_AGNOSTIC_OUTCOMES[key] ?? IN_PROGRESS;
};

export const isCardPayment = (paymentMethod?: string): boolean =>
  CARD_METHODS.includes(normalize(paymentMethod));

export const isBankTransferPayment = (paymentMethod?: string): boolean =>
  BANK_TRANSFER_METHODS.includes(normalize(paymentMethod));

export const expectsBankTransfer = (status?: string, paymentMethod?: string): boolean => {
  if (isCardPayment(paymentMethod)) return false;
  return normalize(status) === FLYWIRE_BANK_TRANSFER_STATUS || isBankTransferPayment(paymentMethod);
};
